import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

/**
 * Family-side client for the case-level follow-up portal (WP-2.2).
 * Every callable is token-scoped: the family never supplies case, funeral
 * home, or tenant ids. Contract:
 * docs/planning/family-follow-up-portal-contract-2026-09-04.md
 */

const functions = getFunctions(firebaseApp, "us-east1");

export type FamilyPortalTimelineEntry = {
  id: string;
  label: string;
  state: "complete" | "current" | "upcoming";
};

export type FamilyPortalMissingItem = {
  id: string;
  kind: "checklist_request" | "vital_correction" | "journey_section";
  title: string;
  requestedAt?: string;
};

export type FamilyPortalPacketState =
  | "needs_signature"
  | "signed"
  | "completed"
  | "voided";

export type FamilyPortalPacketSummary = {
  packetId: string;
  title: string;
  state: FamilyPortalPacketState;
  signingSessionToken?: string;
};

export type FamilyPortalUploadState =
  | "reserved"
  | "pending_scan"
  | "promotion_pending"
  | "clean"
  | "quarantined"
  | "scan_failed"
  | "expired";

export type FamilyPortalUpload = {
  quarantineId: string;
  title: string;
  status: FamilyPortalUploadState;
  supportMessage: string;
  revision: number;
};

export type FamilyPortalMessage = {
  id: string;
  from: "family" | "care_team";
  body: string;
  sentAt: string;
};

export type FamilyPortalProjection = {
  lovedOneName: string;
  funeralHomeName: string;
  timeline: FamilyPortalTimelineEntry[];
  missingItems: FamilyPortalMissingItem[];
  signaturePackets: FamilyPortalPacketSummary[];
  uploads: FamilyPortalUpload[];
  messages: FamilyPortalMessage[];
  memorialTabAvailable: boolean;
  memorialSlug: string;
  linkExpiresAt: string;
};

export type FamilyPortalQuarantineStatus = {
  category: string;
  quarantineId: string;
  revision: number;
  status: FamilyPortalUploadState;
  supportMessage: string;
  title: string;
};

export async function getPublicFamilyPortal(token: string) {
  const callable = httpsCallable<{ token: string }, FamilyPortalProjection>(
    functions,
    "getPublicFamilyPortal",
  );
  return (await callable({ token })).data;
}

export async function reserveFamilyPortalUpload(input: {
  token: string;
  category: string;
  declaredMimeType: string;
  fileName: string;
  sizeBytes: number;
  title: string;
  idempotencyKey: string;
}) {
  const callable = httpsCallable<
    typeof input,
    {
      quarantine: FamilyPortalQuarantineStatus;
      upload: {
        contentType: string;
        expiresAt: string;
        method: "PUT";
        url: string;
      };
    }
  >(functions, "reserveFamilyPortalUpload");
  return (await callable(input)).data;
}

export async function finalizeFamilyPortalUpload(input: {
  token: string;
  quarantineId: string;
}) {
  const callable = httpsCallable<
    typeof input,
    { quarantine: FamilyPortalQuarantineStatus }
  >(functions, "finalizeFamilyPortalUpload");
  return (await callable(input)).data;
}

export async function retryFamilyPortalUploadScan(input: {
  token: string;
  quarantineId: string;
  expectedRevision: number;
}) {
  const callable = httpsCallable<
    typeof input,
    { quarantine: FamilyPortalQuarantineStatus }
  >(functions, "retryFamilyPortalUploadScan");
  return (await callable(input)).data;
}

export async function sendPublicFamilyPortalMessage(input: {
  token: string;
  message: string;
  idempotencyKey: string;
}) {
  const callable = httpsCallable<
    typeof input,
    { messageId: string; state: "received" }
  >(functions, "sendPublicFamilyPortalMessage");
  return (await callable(input)).data;
}

/** The signing route embedded in the projection reuses the existing /sign experience. */
export function familyPortalSigningPath(packet: FamilyPortalPacketSummary) {
  return packet.signingSessionToken
    ? `/sign/${packet.signingSessionToken}`
    : null;
}

export type FamilyPortalOutboundState =
  | "queued_on_device"
  | "sending"
  | "sent"
  | "failed";

/**
 * Truthful outbound copy for family messages (contract-tested strings in
 * functions/src/family-portal-contract.ts). Never claims delivery the
 * callable has not confirmed.
 */
export function describeFamilyPortalOutboundState(
  state: FamilyPortalOutboundState,
) {
  switch (state) {
    case "queued_on_device":
      return "Saved on this device. It will send when the connection returns.";
    case "sending":
      return "Sending…";
    case "sent":
      return "Shared with your care team.";
    case "failed":
      return "Could not send. Your message is still on this device — try again.";
  }
}

export function createFamilyPortalIdempotencyKey(action: string) {
  const random =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `family-portal-${action}-${random}`.replace(/[^A-Za-z0-9_-]/g, "-");
}

export function getFamilyPortalErrorMessage(error: unknown) {
  const code =
    typeof error === "object" && error && "code" in error
      ? String(error.code)
      : "";
  const message =
    error instanceof Error
      ? error.message.replace(/^FirebaseError:\s*/i, "")
      : "";
  if (code.includes("not-found")) {
    return "This link is not available. It may have expired or been replaced — please contact your care team for a new link.";
  }
  if (code.includes("resource-exhausted")) {
    return "You're making too many requests. Please try again in a little while.";
  }
  if (code.includes("failed-precondition")) {
    return (
      message ||
      "That action is not available right now. Please review the page and try again."
    );
  }
  if (code.includes("invalid-argument")) {
    return message || "Please review what you entered and try again.";
  }
  return "We couldn't do that right now. Please try again.";
}
